import { LeadAssignmentRuleFormModel } from '../../../../typings'
import { useFormikContext, useField } from 'formik'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AssigneeOption, selectAllSalesTeamsUsers } from '@user/store/salesTeam'

type Props = {
  name: string
  required?: boolean
  salesTeamIds: number[]
  showUnAssignedOption: boolean
}

export function useData(props: Props) {
  const { name, required, salesTeamIds, showUnAssignedOption } = props

  const ownerList = useSelector(selectAllSalesTeamsUsers)
  const items = useMemo(() => {
    if (salesTeamIds.length === 0) {
      return []
    }

    const unassigned = {
      name: 'Unassigned',
      id: '00000000-0000-0000-0000-000000000000',
      userId: 'unassigned',
      isActive: true,
    }

    if (showUnAssignedOption) {
      return [unassigned, ...ownerList]
    }

    return ownerList
  }, [ownerList, ownerList, salesTeamIds, showUnAssignedOption])

  const {
    isSubmitting,
    values: { ownerIds },
  } = useFormikContext<LeadAssignmentRuleFormModel>()

  const [{}, { error, touched }] = useField<string[]>({
    name,
  })

  const showError =
    required !== undefined
      ? Boolean(required && error && touched)
      : Boolean(error && touched)
  const helperText = showError ? error : ''

  const initialOptions = useMemo(() => {
    if (ownerIds && ownerIds.length > 0) {
      const value = items.filter((x) => ownerIds.includes(x.id))
      if (value) return value
    }
    return []
  }, [items, ownerIds])

  const getLabel = useCallback(
    (option: AssigneeOption) => `${option?.name}`,
    [],
  )

  const getId = useCallback((option: AssigneeOption) => {
    return String(option.id)
  }, [])

  const renderSummary = useCallback((options, text: string) => {
    if (options.length === 1) {
      return options[0].name
    } else {
      return `${options.length} ${text}`
    }
  }, [])

  const isOptionEqual = useCallback(
    (optionA: AssigneeOption, optionB: AssigneeOption) =>
      optionA.id === optionB.id,
    [],
  )

  const matchFilter = useCallback((option, _getLabel, searchValue) => {
    const searchIn = [option.name]
    return searchIn.some((entry) =>
      entry.toLowerCase().includes(searchValue.toLowerCase()),
    )
  }, [])

  return {
    items,
    showError,
    helperText,
    isSubmitting,
    initialOptions,
    renderSummary,
    getId,
    getLabel,
    matchFilter,
    isOptionEqual,
  }
}
